import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { MessagesSquare, Send, Search } from "lucide-react";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";

export default function Conversations() {
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [q, setQ] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef();

  const loadList = async () => { const { data } = await api.get("/conversations"); setConvs(data); };
  useEffect(() => { loadList(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const openConv = async (c) => {
    setActive(c);
    const { data } = await api.get(`/conversations/${c.id}/messages`);
    setMessages(data.messages || []);
    loadList();
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !active) return;
    setSending(true);
    try {
      await api.post(`/conversations/${active.id}/reply`, { body: reply });
      setReply("");
      const { data } = await api.get(`/conversations/${active.id}/messages`);
      setMessages(data.messages || []);
      loadList();
    } catch { toast.error("Could not send"); }
    finally { setSending(false); }
  };

  const filtered = convs.filter((c) => (c.member_name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="sj-page-hero">
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Conversations</h1>
        <p style={{ color: "var(--sj-text-2)" }}>Two-way messages with your members</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" style={{ minHeight: 560 }}>
        {/* List */}
        <div className={`${CARD} p-4 lg:col-span-1 flex flex-col`} data-testid="conversation-list">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "var(--sj-primary)" }} />
            <input data-testid="conv-search" placeholder="Search members..." value={q} onChange={(e) => setQ(e.target.value)}
              className="w-full bg-white border-2 border-[#0B5E4E] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/30 shadow-sm pl-9 pr-3 py-2.5 text-sm" />
          </div>
          <div className="flex-1 overflow-y-auto space-y-1" style={{ maxHeight: 500 }}>
            {filtered.length === 0 && <div className="text-sm text-center py-8" style={{ color: "var(--sj-text-3)" }}>No conversations yet.</div>}
            {filtered.map((c) => (
              <button key={c.id} data-testid={`conv-${c.id}`} onClick={() => openConv(c)}
                className="w-full text-left flex items-center gap-3 p-3 rounded-xl transition-colors"
                style={{ background: active?.id === c.id ? "var(--sj-peach)" : "transparent" }}>
                <div className="h-10 w-10 rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0" style={{ background: "var(--sj-ochre)" }}>
                  {(c.member_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-sm truncate">{c.member_name || "Member"}</span>
                    {c.unread_staff > 0 && <span className="rounded-full px-2 py-0.5 text-[10px] font-bold text-white shrink-0" style={{ background: "var(--sj-primary)" }} data-testid={`conv-unread-${c.id}`}>{c.unread_staff}</span>}
                  </div>
                  <div className="text-xs truncate" style={{ color: "var(--sj-text-3)" }}>
                    {c.last_preview ? (c.last_sender_type === "staff" ? "You: " : "") + c.last_preview : "No messages yet"}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Thread */}
        <div className={`${CARD} lg:col-span-2 flex flex-col`} data-testid="conversation-thread">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 p-10" style={{ color: "var(--sj-text-3)" }}>
              <MessagesSquare className="h-12 w-12" /> Select a conversation to view and reply
            </div>
          ) : (
            <>
              <div className="px-6 py-4 border-b flex items-center gap-3" style={{ borderColor: "var(--sj-border)" }}>
                <div className="h-9 w-9 rounded-full flex items-center justify-center text-white text-sm font-semibold" style={{ background: "var(--sj-ochre)" }}>
                  {(active.member_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                </div>
                <div>
                  <div className="font-semibold">{active.member_name}</div>
                  <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>{active.member_email}</div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-3" style={{ maxHeight: 400 }}>
                {messages.map((m) => {
                  const staff = m.sender_type === "staff";
                  return (
                    <div key={m.id} className={`flex ${staff ? "justify-end" : "justify-start"}`}>
                      <div className="max-w-[75%]">
                        <div className={`text-xs mb-1 ${staff ? "text-right" : ""}`} style={{ color: "var(--sj-text-3)" }}>
                          {staff ? m.sender_name : active.member_name} · {new Date(m.created_at).toLocaleString('en-GB', { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </div>
                        {m.subject && staff && <div className="font-semibold text-sm mb-0.5">{m.subject}</div>}
                        <div className="rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap"
                          style={staff ? { background: "var(--sj-secondary)", color: "#fff", borderTopRightRadius: 4 } : { background: "var(--sj-peach)", color: "var(--sj-text)", borderTopLeftRadius: 4 }}>
                          {m.body}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <form onSubmit={sendReply} className="p-4 border-t flex items-end gap-2" style={{ borderColor: "var(--sj-border)" }}>
                <textarea data-testid="conv-reply-input" rows={1} value={reply} onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(e); } }}
                  placeholder={`Reply to ${active.member_name?.split(" ")[0]}...`}
                  className="flex-1 bg-[#EDF2F4] border border-[#EBE9E4] rounded-2xl focus:ring-2 focus:ring-[#0B5E4E]/20 focus:border-[#0B5E4E] outline-none transition-all px-4 py-3 text-sm resize-none" />
                <button type="submit" disabled={sending} data-testid="conv-reply-send"
                  className="rounded-full h-11 w-11 flex items-center justify-center text-white shrink-0 hover:brightness-105 transition-all disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>
                  <Send className="h-5 w-5" />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
